import { Card } from '@hqo/ui-atoms';
import { SearchX, UserPlus } from 'lucide-react';
import { FormattedMessage } from 'react-intl';
import { useIsSmallViewportWidth } from '@hqo/react-components-library/dist/device';

interface EmptyStateProps {
  filtered?: boolean;
  selectedTimeFrame?: string | null;
}

export const EmptyState = ({ filtered = false, selectedTimeFrame = null }: EmptyStateProps): JSX.Element => {
  const isSmallViewport = useIsSmallViewportWidth();

  if (filtered) {
    return (
      <Card className="border-dashed border-2 bg-gray-50" data-testid="empty-state">
        <div className="flex flex-col items-center justify-center py-8 px-4">
          <div className="w-24 h-24 rounded-full bg-gray-100 flex items-center justify-center mb-6">
            <SearchX className="w-12 h-12 text-gray-400" />
          </div>
          <h2 className="text-2xl font-semibold mb-3 text-gray-900">
            <FormattedMessage id="labels.emptyState.noResults.title" defaultMessage="No results found" />
          </h2>
          <p className="text-center text-gray-600 max-w-md leading-relaxed">
            <FormattedMessage
              id="labels.emptyState.noResults.description"
              defaultMessage="No visits match your current filters. Try adjusting your search criteria or clearing the filters."
            />
          </p>
        </div>
      </Card>
    );
  }

  // Determine which description message to show based on selected time frame
  const descriptionKey =
    selectedTimeFrame === 'today'
      ? 'labels.emptyState.noVisitors.descriptionToday'
      : 'labels.emptyState.noVisitors.description';

  return (
    <Card className="border-dashed border-2 bg-gray-50" data-testid="empty-state">
      <div className="flex flex-col items-center justify-center py-8 px-4">
        <div className="w-24 h-24 rounded-full bg-gray-100 flex items-center justify-center mb-6">
          <UserPlus className="w-12 h-12 text-gray-400" />
        </div>
        <h2 className="text-2xl font-semibold mb-3 text-gray-900">
          <FormattedMessage id="labels.emptyState.noVisitors.title" defaultMessage="No visitors" />
        </h2>
        <p className="text-center text-gray-600 max-w-md leading-relaxed">
          <FormattedMessage
            id={descriptionKey}
            defaultMessage={
              selectedTimeFrame === 'today' ? 'You have no visitors today.' : 'You have no upcoming or past visitors.'
            }
          />
        </p>
        {isSmallViewport ? (
          <p className="text-center text-gray-600 max-w-md leading-relaxed mt-2">
            <FormattedMessage
              id="labels.emptyState.noVisitors.mobileHelper"
              defaultMessage='Click the "+" button at the bottom right to register a new visitor.'
            />
          </p>
        ) : (
          <p className="text-center text-gray-600 max-w-md leading-relaxed mt-2">
            <FormattedMessage
              id="labels.emptyState.noVisitors.desktopHelper"
              defaultMessage='Click "Create visit" at the top right to register a new visitor.'
            />
          </p>
        )}
      </div>
    </Card>
  );
};
